import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';

import { initialsOf } from '../../inbox/inboxShared';
import type { DashboardInboxSummary } from '../types';

const COLLAPSED_COUNT = 5;

type DashboardInboxPanelProps = {
  inbox: DashboardInboxSummary;
};

export function DashboardInboxPanel({ inbox }: DashboardInboxPanelProps) {
  const { instanceId } = useParams<{ instanceId: string }>();
  const inboxBase = `/i/${encodeURIComponent(instanceId ?? 'default')}/inbox`;
  const [expanded, setExpanded] = useState(false);

  const canExpand = inbox.recent.length > COLLAPSED_COUNT;
  const visible = expanded ? inbox.recent : inbox.recent.slice(0, COLLAPSED_COUNT);
  const hiddenCount = inbox.recent.length - COLLAPSED_COUNT;

  return (
    <div className="rounded-xl border border-app-border bg-app-surface p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-semibold text-app-text">Posteingang</h2>
        <Link to={inboxBase} className="inline-flex items-center gap-1 self-start text-xs font-semibold text-blue-600 hover:text-blue-700">
          Posteingang öffnen
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div>
        {inbox.recent.length === 0 ? (
          <p className="py-4 text-center text-xs text-slate-400">Keine E-Mails von heute.</p>
        ) : (
          <>
            <ul className="space-y-1">
              {visible.map((item) => (
                <li key={item.id}>
                  <Link
                    to={`${inboxBase}/${encodeURIComponent(item.id)}`}
                    className="flex items-center gap-2 rounded-lg px-1.5 py-2 transition-colors hover:bg-app-hover sm:gap-3 sm:px-2"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-app-surface-muted text-[10px] font-semibold text-app-text-subtle">
                      {initialsOf(item.sender)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs font-semibold text-app-text">{item.sender}</span>
                      <span className="block truncate text-xs text-app-text-subtle">{item.subject}</span>
                    </span>
                    <span className="shrink-0 text-[10px] text-slate-400 sm:text-[11px]">{item.relativeTime}</span>
                  </Link>
                </li>
              ))}
            </ul>
            {canExpand ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg py-2 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50"
              >
                {expanded ? (
                  <>
                    Weniger anzeigen
                    <ChevronUp className="h-3.5 w-3.5" />
                  </>
                ) : (
                  <>
                    {hiddenCount} weitere anzeigen
                    <ChevronDown className="h-3.5 w-3.5" />
                  </>
                )}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
